import { Suspense, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { Command } from 'cmdk';
import { AlertTriangle, Loader2, Menu as MenuIcon, X } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { isAdminOnlyPath, navGroups, visibleNavGroups } from './nav';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ui/empty';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';
import { PaymentsSetupBanner } from '@/features/payments/PaymentsSetupBanner';
import { PlanBanner } from '@/features/settings/PlanBanner';
import {
  SessionContext,
  hasSeenSession,
  useBillingNouns,
  useSession,
  useSessionQuery
} from '@/hooks/useSession';
import { useGeneralSettings, useSubscription } from '@/hooks/useSettings';
import { useBillables, useClients } from '@/hooks/useReference';
import { useEnvironmentTag, useFeatureFlags } from '@/hooks/useConfig';
import { useSetup, useSetupAction, useSetupApplies } from '@/hooks/useSetup';
import { SignInCard } from '@/features/auth/SignInCard';
import { BugReporterProvider } from '@/features/bug-report/BugReporter';
import { SetupGuide } from '@/features/setup';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

/**
 * Is this the Electron shell rather than a browser tab?
 *
 * `apps/electron` sets no custom user agent (see its window.ts), so Electron's
 * default one applies and carries `Electron/<version>`. That is the whole
 * signal — there is no preload script and no injected global to check, by
 * design: the renderer is just the deployed website.
 */
const IS_DESKTOP_SHELL =
  typeof navigator !== 'undefined' && /\bElectron\//.test(navigator.userAgent);

/**
 * The auth gate and the signed-in chrome. `chromeless` keeps the gate (session
 * fetch, 401 handling, SessionContext) and drops everything else: no sidebar,
 * no mobile top bar, no palette, no first-run redirect. It exists for the
 * screens that need a session but must not look like the app yet: /welcome,
 * and the /onboarding redirect that finishes a Stripe Checkout.
 */
export function AppShell({ chromeless = false }: { chromeless?: boolean }) {
  const { data: session, isPending, error, refetch } = useSessionQuery();
  const { pathname } = useLocation();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const envTag = useEnvironmentTag();

  // Tab title carries the same tag as the brand. The marketing pages set their
  // own title and restore the previous one on unmount, and they mount outside
  // this shell, so there is nothing here to fight.
  useEffect(() => {
    document.title = envTag ? `Billable (${envTag})` : 'Billable';
  }, [envTag]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
        return;
      }
      // Escape's backstop. The palette handles Escape itself (see CommandPalette
      // below) and stops the event there, so this only runs when the palette
      // never saw it — which happens when a click on dead space inside the
      // panel drops focus to <body>. It is safe to leave registered always: it
      // closes only the palette, closing an already-closed palette is a no-op
      // React bails out of, and it does not preventDefault, so a Radix dialog's
      // own Escape handling is untouched.
      if (e.key === 'Escape') setPaletteOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (isPending) {
    // A browser that has never held a session is almost certainly about to be
    // sent to /landing; drawing the app's chrome first and then tearing it
    // down is the flash a visitor remembers. Blank page for them, the
    // skeleton for a customer whose session is just taking a moment.
    if (chromeless || !hasSeenSession()) return <div className="h-full bg-[var(--page)]" />;
    return <BootSpinner />;
  }

  if (error) {
    const unauthorized = error instanceof ApiError && error.isAuth;
    // Signed out is not an error state, it is a visitor. In a BROWSER, hand
    // them the landing page rather than a card apologizing for a 401. No loop:
    // /landing is registered outside this shell, so it never mounts AppShell
    // again.
    //
    // In the DESKTOP SHELL, do not. Somebody who downloaded, installed and
    // opened the app has already been sold; showing them the marketing site
    // inside their own app window — pricing, "Sign in" in a nav bar, a hero
    // video — reads as the app failing to load. They get the sign-in card
    // below, which is what this branch was always for and which was
    // unreachable until now: the redirect above ran first, so the `unauthorized`
    // arm of the ternary that follows could never render.
    if (unauthorized && !IS_DESKTOP_SHELL) return <Navigate to="/landing" replace />;
    return (
      <div className="flex h-full items-center justify-center p-6">
        {unauthorized ? (
          <div className="flex w-full max-w-sm flex-col items-center">
            <SignInCard redirectTo={window.location.pathname} />
            {/*
              Google OAuth cannot complete against a local server, and a dev
              database has no real credentials anyway. Dev-only —
              `import.meta.env.DEV` is compiled out of a production build.
            */}
            {import.meta.env.DEV && (
              <a
                className="mt-3 block text-xs text-muted underline underline-offset-2"
                href="/dev/sign-in"
              >
                Dev: sign in
              </a>
            )}
          </div>
        ) : (
          <ErrorState
            title="Couldn’t load your account"
            message={error instanceof Error ? error.message : undefined}
            onRetry={() => void refetch()}
          />
        )}
      </div>
    );
  }

  if (!session) return null;

  return (
    <SessionContext.Provider value={session}>
      {chromeless ? (
        <div className="scroll-thin h-full overflow-y-auto">
          <RouteErrorBoundary key={pathname}>
            <Suspense fallback={<div className="h-full bg-[var(--page)]" />}>
              <Outlet />
            </Suspense>
          </RouteErrorBoundary>
        </div>
      ) : (
        <FirstRunGate>
          <AppChrome paletteOpen={paletteOpen} setPaletteOpen={setPaletteOpen} />
        </FirstRunGate>
      )}
    </SessionContext.Provider>
  );
}

function AppChrome({
  paletteOpen,
  setPaletteOpen
}: {
  paletteOpen: boolean;
  setPaletteOpen: (v: boolean) => void;
}) {
  const { pathname } = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const envTag = useEnvironmentTag();
  useHideLegacySetup();

  return (
    <BugReporterProvider>
      <div className="flex h-full overflow-hidden">
        <div className="hidden md:block">
          <Sidebar onOpenPalette={() => setPaletteOpen(true)} />
        </div>

        {mobileNavOpen ? (
          <div className="fixed inset-0 z-50 md:hidden">
            <div
              className="absolute inset-0 bg-zinc-950/40"
              onClick={() => setMobileNavOpen(false)}
            />
            <div className="absolute inset-y-0 left-0" onClick={() => setMobileNavOpen(false)}>
              <Sidebar onOpenPalette={() => setPaletteOpen(true)} />
            </div>
          </div>
        ) : null}

        <div className="scroll-thin flex min-w-0 flex-1 flex-col overflow-y-auto [scrollbar-gutter:stable]">
          <div className="flex h-14 shrink-0 items-center gap-2 border-b border-[var(--line)] px-4 md:hidden">
            <Button
              variant="ghost"
              size="iconSm"
              aria-label="Open navigation"
              onClick={() => setMobileNavOpen((v) => !v)}
            >
              {mobileNavOpen ? <X /> : <MenuIcon />}
            </Button>
            <span className="text-base font-semibold tracking-tight">
              Billable
              {envTag ? <span className="font-normal text-muted"> ({envTag})</span> : null}
            </span>
          </div>

          {/*
            Above the routed content and inside the scroll container, so it
            scrolls away rather than eating vertical space on every screen. It
            renders nothing once the team can take payments.
          */}
          <PlanBanner />
          <PaymentsSetupBanner />

          {/* Keyed on pathname so navigating away clears a crashed route. */}
          <RouteErrorBoundary key={pathname}>
            <Suspense fallback={<RouteSkeleton />}>
              {/*
                The backstop. Non-admins never see a link to an admin-only
                route, but they can still type the URL, so the wall goes here —
                once, driven by the same `adminOnly` flag in nav.ts that hides
                the link. Pages don't gate themselves.
              */}
              <AdminOnly>
                <Outlet />
              </AdminOnly>
            </Suspense>
          </RouteErrorBoundary>
        </div>

        {/*
          The setup guide, once for the whole app. Its rail is a 300px flow
          element, so it sits here as a flex sibling to the right of the main
          area; the chip, the coach mark and the phone sheet are fixed and do
          not care where they mount. Renders nothing once hidden.
        */}
        <SetupGuide />

        <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      </div>
    </BugReporterProvider>
  );
}

/**
 * Opens the setup guide for a brand-new team once. "Brand new" means the
 * session has a team and BOTH the clients list and the billing parties list
 * have loaded empty; a team with a single client or payer never sees it. The
 * sessionStorage flag is written before navigating so a team that hides the
 * guide (or still has no clients) is not sent to it again this session. Only
 * mounted by the full-chrome shell, so /onboarding, /welcome and the public
 * routes (which never mount the shell) are never redirected; the pathname
 * check is a backstop for the same paths.
 */
export const ONBOARDED_KEY = (teamId: string) => 'billable.onboarded:' + teamId;

function isRedirectExempt(pathname: string) {
  return (
    pathname === '/onboarding' ||
    pathname === '/welcome' ||
    pathname.startsWith('/pay/') ||
    pathname === '/desktop-auth' ||
    pathname === '/landing' ||
    pathname === '/download' ||
    pathname === '/mobile'
  );
}

function hasOnboardedFlag(teamId: string): boolean {
  try {
    return sessionStorage.getItem(ONBOARDED_KEY(teamId)) !== null;
  } catch {
    return false;
  }
}

/**
 * A team that has ever had a client or a billing party is established: the
 * guide will never be opened for it unasked, so there is nothing to wait for. Remembered
 * in localStorage (per team, survives the tab) so a returning customer's reload
 * renders the chrome at once instead of holding the page until two reference
 * fetches answer. The session flag above only covers a team that was actually
 * sent to onboarding, which is why every other team was waiting on every load.
 */
export const ESTABLISHED_KEY = (teamId: string) => 'billable.established:' + teamId;

function isEstablished(teamId: string): boolean {
  try {
    return localStorage.getItem(ESTABLISHED_KEY(teamId)) === '1';
  } catch {
    return false;
  }
}

function rememberEstablished(teamId: string) {
  try {
    localStorage.setItem(ESTABLISHED_KEY(teamId), '1');
  } catch {
    // Storage unavailable; the next load waits on the lists again, which is
    // slower but still correct.
  }
}

/**
 * The first-run decision, made BEFORE the chrome paints. It used to be an
 * effect that ran after the timesheet had already rendered, so a new team saw
 * the app flash up and then get yanked to /onboarding.
 *
 * - `ready`: render the app. Exempt path, no team, someone who joined the
 *   team by invite (onboarding is the creator's), flag already set this
 *   session (no wait for returning users), any client or billing party exists,
 *   or either list failed to load (never block the app on a reference fetch).
 * - `pending`: flag unset and a list is still loading.
 * - `onboard`: both lists loaded empty and the flag was unset. The flag is
 *   written by the gate so the guide is opened once, not on every render.
 */
function useFirstRunDecision(): 'ready' | 'pending' | 'onboard' {
  const session = useSession();
  const { pathname } = useLocation();
  const teamId = session.team?.id;
  const clients = useClients();
  const billables = useBillables();

  if (!teamId || isRedirectExempt(pathname) || hasOnboardedFlag(teamId)) return 'ready';
  if (!session.isTeamCreator) return 'ready';
  if (isEstablished(teamId)) return 'ready';
  if (clients.isError || billables.isError) return 'ready';
  if (!clients.isSuccess || !billables.isSuccess) return 'pending';
  if (clients.data.length > 0 || billables.data.length > 0) return 'ready';
  return 'onboard';
}

/**
 * Writes the established flag once either list is known to be non-empty. Kept
 * out of the decision hook so the hook stays a pure read; the lists are already
 * being fetched by the decision (and by the screens), so this adds no request.
 */
function useRememberEstablished(teamId: string | undefined) {
  const clients = useClients();
  const billables = useBillables();
  const established =
    (clients.isSuccess && clients.data.length > 0) ||
    (billables.isSuccess && billables.data.length > 0);
  useEffect(() => {
    if (teamId && established) rememberEstablished(teamId);
  }, [teamId, established]);
}

/**
 * A new team starts its trial in Stripe Checkout, card and all. One with no
 * subscription yet (it backed out of Checkout, closed the tab, or typed a URL)
 * is sent to the plan step instead of into the app. `cardRequired` is false for
 * teams from before card-up-front trials, so their no-card trial is untouched.
 * Only the team's creator is sent: the plan step is part of their sign-up, and
 * an invitee cannot pick the team's plan.
 */
function useNeedsCard(): boolean {
  const { pathname } = useLocation();
  const subscription = useSubscription();
  const session = useSession();
  // A demo team never pays; the card gate would bounce it to the plan step.
  if (session.team?.isDemo || !session.isTeamCreator) return false;
  if (isRedirectExempt(pathname) || pathname === '/subscription' || !subscription.data) return false;
  return subscription.data.cardRequired && subscription.data.tier === 'trial';
}

/**
 * A new team lands in the real app, not a wizard: the 'onboard' decision
 * writes the session flag, renders the chrome, and navigates once (replace) to
 * the timesheet with `?setup=1`, which the setup guide reads to open its rail.
 * A team whose guide is already finished (every step skipped, say) gets the
 * flag but no redirect.
 */
function FirstRunGate({ children }: { children: ReactNode }) {
  const session = useSession();
  const navigate = useNavigate();
  const decision = useFirstRunDecision();
  const needsCard = useNeedsCard();
  // Fetched here, beside the decision's lists, so the two load in parallel
  // rather than one after the other.
  const setup = useSetup();
  const setupApplies = useSetupApplies();
  const teamId = session.team?.id;
  useRememberEstablished(teamId);

  const opened = useRef(false);
  const setupLoading = setup.isPending;
  const setupFinished = !!setup.data?.finished;
  useEffect(() => {
    // 'onboard' implies the creator, so the setup query is running: wait for it.
    if (decision !== 'onboard' || !teamId || opened.current || setupLoading) return;
    opened.current = true;
    try {
      sessionStorage.setItem(ONBOARDED_KEY(teamId), '1');
    } catch {
      // Storage unavailable (private mode, blocked). The ref above still
      // keeps this to once per mount.
    }
    if (!setupFinished) navigate('/timesheet?setup=1', { replace: true });
  }, [decision, teamId, setupLoading, setupFinished, navigate]);

  if (needsCard) return <Navigate to="/welcome?step=plan" replace />;
  // One spinner for the whole boot: the session fetch shows the same one, so a
  // first load is spinner, then the app with its setup rail in the same paint.
  // The rail is a 300px flow sibling of the page, so drawing the page before
  // the setup state arrives shifts everything left a beat later. Only the
  // first load waits (`isPending` is "no data yet"); refetches keep the page.
  // An invitee has no setup query to wait on: it never runs for them.
  if (decision === 'pending' || (setupApplies && setup.isPending)) return <BootSpinner />;
  return <>{children}</>;
}

/**
 * Teams that predate the setup guide must not meet it uninvited. The guide's
 * state is derived from data, so an established team would otherwise see a
 * rail with most steps already ticked. Once per team per session, the first
 * time the setup state arrives, hide it when ALL of these hold:
 *
 * - the team is established (the localStorage flag, or a non-empty clients or
 *   billing parties list);
 * - nobody pressed "Start over" (`startedOverAt` is null);
 * - the client, entry and invoice steps were already done at first load, so
 *   the data came from before the guide, not through it;
 * - the session flag `billable.setupLegacyHidden:<teamId>` is unset.
 *
 * The flag is set afterwards either way, so a team the check decided to leave
 * alone is not re-checked, and "show" from Settings or /setup is not undone on
 * the next load. A team that is already hidden is left as it is.
 */
const SETUP_LEGACY_HIDDEN_KEY = (teamId: string) => 'billable.setupLegacyHidden:' + teamId;

function useHideLegacySetup() {
  const session = useSession();
  const teamId = session.team?.id;
  const setup = useSetup();
  const action = useSetupAction();
  const clients = useClients();
  const billables = useBillables();
  const state = setup.data;
  const hide = action.mutate;

  const checked = useRef(false);
  useEffect(() => {
    if (!teamId || !state || checked.current) return;
    let flagged = false;
    try {
      flagged = sessionStorage.getItem(SETUP_LEGACY_HIDDEN_KEY(teamId)) !== null;
    } catch {
      // Storage unavailable: the check runs once per mount instead.
    }
    if (flagged) {
      checked.current = true;
      return;
    }
    const established =
      isEstablished(teamId) ||
      (clients.isSuccess && clients.data.length > 0) ||
      (billables.isSuccess && billables.data.length > 0);
    // Wait for the lists before concluding the team is new.
    if (!established && (!clients.isSuccess || !billables.isSuccess)) {
      if (!clients.isError && !billables.isError) return;
    }
    checked.current = true;
    const doneAtLoad = (id: 'client' | 'entry' | 'invoice') =>
      state.steps.find((s) => s.id === id)?.done ?? false;
    const legacy =
      established &&
      state.startedOverAt === null &&
      doneAtLoad('client') &&
      doneAtLoad('entry') &&
      doneAtLoad('invoice');
    if (legacy && !state.hidden) hide('hide');
    try {
      sessionStorage.setItem(SETUP_LEGACY_HIDDEN_KEY(teamId), '1');
    } catch {
      // Storage unavailable; see above.
    }
  }, [teamId, state, clients, billables, hide]);
}

/** The boot loader: session, first-run decision and setup state, one wait. */
function BootSpinner() {
  return (
    <div
      className="flex h-full items-center justify-center bg-[var(--page)]"
      role="status"
      aria-label="Loading"
    >
      <Loader2 className="size-6 animate-spin text-muted" aria-hidden />
    </div>
  );
}

function RouteSkeleton() {
  return (
    <div className="mx-auto w-full max-w-6xl px-6 py-8">
      <div className="skeleton h-9 w-56" />
      <div className="skeleton mt-2 h-5 w-80" />
      <div className="skeleton mt-6 h-96 w-full rounded-2xl" />
    </div>
  );
}

/**
 * Command palette. Driven off the same `navGroups()` definition as the sidebar
 * so the two can't drift apart.
 */
function CommandPalette({
  open,
  onOpenChange
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const navigate = useNavigate();
  const nouns = useBillingNouns();
  const session = useSession();
  const settings = useGeneralSettings().data;
  const flags = useFeatureFlags();
  const groups = visibleNavGroups(navGroups(nouns), {
    isAdmin: session.isAdmin,
    isPlatformAdmin: session.isPlatformAdmin,
    showOriginalTimesheets: Boolean(settings?.showOriginalTimesheetEntries),
    devOnly: flags.devOnly,
    features: session.flags
  });

  const go = useCallback(
    (to: string) => {
      onOpenChange(false);
      navigate(to);
    },
    [navigate, onOpenChange]
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh]">
      <div className="absolute inset-0 bg-zinc-950/40 backdrop-blur-[2px]" onClick={() => onOpenChange(false)} />
      <Command
        loop
        /*
          Escape has to be handled here. Unlike the shared Dialog in
          components/ui/dialog.tsx, this palette is a bare cmdk `Command` in a
          plain div rather than a Radix dialog, so there is no
          `onEscapeKeyDown` closing it for free — and cmdk itself only handles
          the arrow/enter navigation keys. A listener on the palette rather
          than on window is deliberate: a global one would fire for every other
          dialog on the page too.
        */
        onKeyDown={(e) => {
          if (e.key !== 'Escape') return;
          e.preventDefault();
          e.stopPropagation();
          onOpenChange(false);
        }}
        className="relative w-[min(36rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-[var(--line)] bg-[var(--surface)] shadow-2xl"
      >
        <Command.Input
          autoFocus
          placeholder="Jump to…"
          className="w-full border-b border-[var(--line)] bg-transparent px-4 py-3.5 text-base outline-none placeholder:text-[var(--text-subtle)]"
        />
        <Command.List className="scroll-thin max-h-80 overflow-y-auto p-2">
          <Command.Empty className="px-3 py-8 text-center text-sm text-muted">
            Nothing matches that.
          </Command.Empty>
          {groups.map((group) => (
            <Command.Group
              key={group.label}
              heading={group.label}
              className="[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-[var(--text-subtle)]"
            >
              {group.items.map((item) => (
                <Command.Item
                  key={item.to}
                  value={item.to}
                  keywords={[item.label, ...(item.keywords ?? [])]}
                  onSelect={() => go(item.to)}
                  className={cn(
                    'flex cursor-pointer items-center gap-2.5 rounded-xl px-2.5 py-2 text-sm',
                    'data-[selected=true]:bg-[var(--surface-hover)]'
                  )}
                >
                  <item.icon className="size-4 text-[var(--text-muted)]" />
                  {item.label}
                </Command.Item>
              ))}
            </Command.Group>
          ))}
        </Command.List>
      </Command>
    </div>
  );
}

// ------------------------------------------------------------- page chrome

export function Page({
  title,
  description,
  actions,
  children,
  wide
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <div className={cn('mx-auto w-full px-6 py-8', wide ? 'max-w-[96rem]' : 'max-w-6xl')}>
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-semibold tracking-tight">{title}</h1>
          {description ? <p className="mt-1 text-sm text-muted">{description}</p> : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </header>
      {children}
    </div>
  );
}

/**
 * Renders the current route, or the admins-only wall when the route is declared
 * `adminOnly` in nav.ts and the viewer is not an admin. Mounted once around the
 * router outlet — a page should never wrap itself in this, because then the set
 * of admin screens would live in two places.
 */
export function AdminOnly({ children }: { children: ReactNode }) {
  const session = useSession();
  const { pathname } = useLocation();
  if (session.isAdmin || !isAdminOnlyPath(pathname)) return <>{children}</>;
  return (
    <div className="mx-auto w-full max-w-6xl px-6 py-8">
      <div className="flex flex-col items-center rounded-2xl border border-[var(--line)] bg-[var(--surface)] px-6 py-16 text-center shadow-sm">
        <div className="mb-4 flex size-12 items-center justify-center rounded-2xl bg-amber-50 text-amber-600 dark:bg-amber-950/50 dark:text-amber-400">
          <AlertTriangle className="size-5" />
        </div>
        <h2 className="text-base font-semibold tracking-tight">Admins only</h2>
        <p className="mt-1 max-w-sm text-sm text-muted">
          Ask a team admin to give you access if you need this screen.
        </p>
      </div>
    </div>
  );
}
